import Icon from './Icon.jsx';

/**
 * The card grid — equal tiles, no hierarchy.
 *
 * Every card is the same size on purpose. The moment one is bigger the grid
 * stops being a choice and becomes a recommendation with alternatives, which is
 * what the old featured tile was doing. Somebody opening this should be able to
 * go anywhere without the layout having an opinion.
 *
 * Presentational only: it takes cards and renders them. What each card is, what
 * it says and where it goes belongs to the screen using it, because that is the
 * thing that knows how many questions are left or whether anything is due.
 *
 * Each card carries one accent hue, applied to its icon, border and glow and
 * nowhere else — the title and blurb stay on the app's normal ink, so nine
 * different colours do not become nine different reading experiences.
 *
 * A card shape:
 *   { id, icon, accent, title, blurb, note, tone?, ready?, onSelect }
 *
 *   note   the line at the bottom right — a fixed XP reward, or live state
 *   tone   'reward' sets it in gold; anything else leaves it quiet, so a real
 *          number is never dressed up as a prize
 *   ready  false greys the card and disables it, at full size
 */
export default function PathGrid({ heading, cards }) {
  return (
    <section className="paths-section" aria-labelledby="paths-heading">
      {heading ? (
        <h2 className="paths-heading" id="paths-heading">
          <span>{heading}</span>
        </h2>
      ) : null}

      <div className="path-grid">
        {cards.map((card, i) => {
          const ready = card.ready !== false;
          return (
            <button
              key={card.id}
              type="button"
              className={`path-card path-card--${card.accent}${ready ? '' : ' is-empty'}`}
              // Staggered, so the shimmer does not sweep every card in lockstep
              // and read as one flashing panel rather than nine live ones.
              style={{ '--shimmer-delay': `${(i % 5) * 1.3}s` }}
              onClick={ready ? card.onSelect : undefined}
              disabled={!ready}
            >
              <span className="path-card__icon" aria-hidden="true">
                <Icon name={card.icon} size={28} />
              </span>
              <span className="path-card__title">{card.title}</span>
              <span className="path-card__blurb">{card.blurb}</span>
              <span
                className={`path-card__xp${card.tone === 'reward' && ready ? ' is-reward' : ''}`}
              >
                {card.note}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
